import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { base44 } from "@voult/api/base44Client";
import { Copy, Check, Trash2, Loader2, CreditCard, ShieldCheck, ArrowLeft } from "lucide-react";

const THEMES = {
  midnight: "from-slate-900 via-indigo-950 to-slate-900",
  emerald: "from-emerald-900 via-teal-900 to-slate-900",
  sunset: "from-rose-900 via-orange-900 to-slate-900",
  royal: "from-violet-900 via-purple-950 to-slate-900",
  graphite: "from-zinc-800 via-zinc-900 to-black",
};

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</span>
      <span className={`text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}

export default function TokenDetails() {
  const navigate = useNavigate();
  const id = new URLSearchParams(window.location.search).get("id");
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!id) return setLoading(false);
    base44.entities.Card.get(id).then(setCard).finally(() => setLoading(false));
    base44.entities.ActivityLog.create({ action: "token_viewed", entity_type: "Card", entity_id: id, details: "Token details viewed" });
  }, [id]);

  const copy = async () => {
    await navigator.clipboard.writeText(card.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const remove = async () => {
    if (!confirm("Remove this card from your vault?")) return;
    setDeleting(true);
    await base44.entities.Card.delete(id);
    await base44.entities.ActivityLog.create({ action: "card_deleted", entity_type: "Card", entity_id: id, details: "Card removed from vault" });
    navigate("/voult/vault-list");
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!card) return <div className="p-6 text-slate-500">Card not found. <button onClick={() => navigate("/voult/vault-list")} className="text-indigo-600 hover:underline">Back to vault</button></div>;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <button onClick={() => navigate("/voult/vault-list")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6 transition">
        <ArrowLeft className="h-4 w-4" /> Back to vault
      </button>

      <div className={`rounded-3xl bg-gradient-to-br ${THEMES[card.color_theme] || THEMES.midnight} p-6 text-white mb-6 relative overflow-hidden`}>
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/5 blur-2xl" />
        <div className="flex justify-between items-start mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-white/50">{card.nickname || "Voult Card"}</p>
            <p className="text-sm text-white/80 mt-0.5">{card.cardholder_name}</p>
          </div>
          <span className="text-xs font-bold uppercase tracking-wider bg-white/10 px-3 py-1 rounded-full">{card.brand}</span>
        </div>
        <p className="font-mono text-xl tracking-widest">{card.masked_number}</p>
        <div className="mt-6 flex justify-between items-end">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/40">Expires</p>
            <p className="font-mono text-sm">{card.expiry_month}/{card.expiry_year}</p>
          </div>
          <CreditCard className="h-8 w-8 text-white/20" />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-4">
        <h2 className="text-sm font-semibold text-slate-900 mb-3">Token Information</h2>
        <Row label="Token" value={card.token} mono />
        <Row label="Masked Number" value={card.masked_number} mono />
        <Row label="Last 4" value={card.last4} mono />
        <Row label="Brand" value={card.brand?.toUpperCase()} />
        <Row label="Cardholder" value={card.cardholder_name} />
        <Row label="Expiry" value={`${card.expiry_month}/${card.expiry_year}`} />
        <Row label="Added" value={card.created_date ? new Date(card.created_date).toLocaleDateString() : "—"} />
      </div>

      <div className="flex items-center gap-2 rounded-xl bg-emerald-50 p-3 mb-6">
        <ShieldCheck className="h-4 w-4 text-emerald-600 flex-shrink-0" />
        <p className="text-xs text-slate-600">This token is opaque. The original PAN is held only encrypted by CITIDAN and is never shown here.</p>
      </div>

      <div className="flex gap-3">
        <button data-testid="voult-charge-card" onClick={() => navigate(`/voult/checkout?card=${card.id}`)}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 transition">
          <CreditCard className="h-4 w-4" /> Charge card
        </button>
        <button onClick={copy}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition">
          {copied ? <><Check className="h-4 w-4 text-emerald-600" /> Copied</> : <><Copy className="h-4 w-4" /> Copy token</>}
        </button>
        <button onClick={remove} disabled={deleting}
          className="flex items-center gap-2 rounded-xl border border-rose-200 px-4 py-2.5 text-sm font-medium text-rose-600 hover:bg-rose-50 transition disabled:opacity-50">
          {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          Remove
        </button>
      </div>
    </div>
  );
}